import React, { useMemo, useEffect } from 'react';
import { DashboardChecklistProvider, useDashboardChecklistContext } from './DashboardChecklist.provider';
import { Box, Flex } from '@withjoy/joykit';
import { ChecklistProgressBar, ChecklistProgressBarHidableContent } from './components/ChecklistProgressBar';
import { pxToRem } from '@withjoy/joykit/theme';
import { differenceInCalendarDays } from 'date-fns';
import { StyledExpandableChecklist } from './DashboardChecklist.styles';
import { ChecklistItem } from './components/ChecklistItem';

const ExpandableChecklist: React.FC = ({ children }) => {
  return (
    <StyledExpandableChecklist>
      <Box overflow={'hidden'}>{children}</Box>
    </StyledExpandableChecklist>
  );
};

const DashboardChecklistInner: React.FC = () => {
  const { isChecklistExpanded, toggleChecklist, checklistData, checklistDataLoading, checklistItemsCompleted, checklistItemsTotal } = useDashboardChecklistContext();

  useEffect(() => {
    if (checklistDataLoading) return;

    toggleChecklist();
  }, [checklistDataLoading, toggleChecklist]);

  const calculateProgress = useMemo(() => {
    return (checklistItemsCompleted / checklistItemsTotal) * 100;
  }, [checklistItemsCompleted, checklistItemsTotal]);

  const daysUntilEvent = useMemo(() => {
    if (!checklistData?.eventByName?.info.finalizedDate) return 0;

    const today = Date.now();
    return differenceInCalendarDays(checklistData?.eventByName?.info.finalizedDate?.milliseconds, today);
  }, [checklistData]);

  return (
    <Flex
      className={isChecklistExpanded ? 'expanded' : 'collapsed'}
      width={375}
      boxShadow={'0px 3px 8px 0px rgba(0, 0, 0, 0.03), 0px 10px 60px -20px rgba(45, 41, 37, 0.03), 0px 9px 36px -30px rgba(0, 0, 0, 0.04)'}
      borderRadius={pxToRem(12)}
      paddingBottom={isChecklistExpanded ? 0 : 7}
      flexDirection={'column'}
      backgroundColor={'white'}
      position={'relative'}
    >
      <ChecklistProgressBar
        isExpanded={isChecklistExpanded}
        toggle={toggleChecklist}
        progressPercentage={calculateProgress}
        daysUntilEvent={daysUntilEvent}
        showSkeleton={checklistDataLoading}
      />
      <ExpandableChecklist>
        <ChecklistProgressBarHidableContent completedItems={checklistItemsCompleted} daysUntilEvent={daysUntilEvent} />
        {/* Placeholder */}
        <ChecklistItem />
        <ChecklistItem />
        <ChecklistItem />
        <ChecklistItem />
        <ChecklistItem />
        <ChecklistItem />
      </ExpandableChecklist>
    </Flex>
  );
};

export const DashboardChecklist: React.FC<{
  eventHandle: string;
}> = ({ eventHandle }) => {
  return (
    <DashboardChecklistProvider eventHandle={eventHandle}>
      <DashboardChecklistInner />
    </DashboardChecklistProvider>
  );
};
